/**
 * Shared visual primitives for S16 — Audit Log.
 *
 * Entity chip · severity pill · hash badge · chain-status indicator.
 */
import React from "react";
import { cn } from "@/lib/utils";

const ENTITY_TONE = {
  request:     "bg-tralenex-50 text-tralenex-900",
  policy:      "bg-opportunity/10 text-opportunity",
  agent:       "bg-gold-300/15 text-gold-600",
  opportunity: "bg-tralenex-50 text-tralenex-900",
  incident:    "bg-risk-high/10 text-risk-high",
  framework:   "bg-tralenex-50 text-tralenex-900",
  vendor:      "bg-inset text-ink-secondary",
  report:      "bg-risk-low/10 text-risk-low",
  system:      "bg-inset text-ink-secondary",
  session:     "bg-inset text-ink-secondary",
};

const ENTITY_LABEL = {
  request: "Request", policy: "Policy", agent: "Agent", opportunity: "Opportunity",
  incident: "Incident", framework: "Framework", vendor: "Vendor", report: "Report",
  system: "System", session: "Session",
};

/** Small uppercase pill identifying the entity type of an audit entry. */
export function EntityChip({ type }) {
  return (
    <span
      data-testid={`audit-entity-${type}`}
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
        ENTITY_TONE[type] || "bg-inset text-ink-secondary",
      )}
    >
      {ENTITY_LABEL[type] || type}
    </span>
  );
}

/** Pill for one of: low · med · high · critical. */
export function SeverityPill({ severity }) {
  const cfg = {
    low:      { label: "Low",      cls: "bg-risk-low/10 text-risk-low",    dot: "bg-risk-low"  },
    med:      { label: "Med",      cls: "bg-risk-med/15 text-risk-med",    dot: "bg-risk-med"  },
    high:     { label: "High",     cls: "bg-risk-high/10 text-risk-high",  dot: "bg-risk-high" },
    critical: { label: "Critical", cls: "bg-risk-high/15 text-risk-high",  dot: "bg-risk-high animate-pulse" },
  }[severity] || { label: severity, cls: "bg-inset text-ink-secondary", dot: "bg-ink-disabled" };
  return (
    <span
      data-testid={`audit-severity-${severity}`}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
        cfg.cls,
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", cfg.dot)} />
      {cfg.label}
    </span>
  );
}

/** Mono hash badge (truncated, copyable look). */
export function HashBadge({ value, label, testId }) {
  return (
    <span
      data-testid={testId}
      title={value}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-inset font-mono text-[10px] tabular-nums text-ink-secondary"
    >
      {label && <span className="text-ink-disabled uppercase tracking-[0.06em] text-[9px] font-bold">{label}</span>}
      <span>{value}</span>
    </span>
  );
}

/** Pulsing dot indicator for chain-integrity status. */
export function ChainStatusDot({ status }) {
  const cfg = {
    verified: { cls: "bg-risk-low",  ring: "ring-risk-low/30",  label: "Chain verified" },
    verifying:{ cls: "bg-tralenex-600 animate-pulse", ring: "ring-tralenex-600/30", label: "Verifying chain…" },
    broken:   { cls: "bg-risk-high", ring: "ring-risk-high/30", label: "Chain integrity error" },
  }[status] || { cls: "bg-ink-disabled", ring: "ring-inset", label: status };
  return (
    <span
      data-testid={`audit-chain-status-${status}`}
      className="inline-flex items-center gap-1.5 text-meta font-medium"
    >
      <span className={cn("w-2 h-2 rounded-full ring-2", cfg.cls, cfg.ring)} />
      {cfg.label}
    </span>
  );
}
